import { StaffContactFields } from 'src/components/AccountAccess/StaffContactFields';
import { useStaffLoginContacts } from 'src/components/AccountAccess/useStaffLoginContacts';
import { useEffect, useState } from 'react';
import { Creator, EmployeeType } from 'src/domain/creator';
import { mutate } from 'swr';
import container from 'src/container';
import Input from 'src/components/FormComponents/Input';
import { EnvelopeIcon, PhoneIcon } from 'src/components/Icons/icons';
import { useNotification } from 'src/components/Notification/useNotfication';
import { Section } from 'src/components/Section';
import { Button } from 'src/components/Buttons/Button';
import { CreatorAvatarInput } from 'src/components/FormComponents/CreatorAvatarInput';
import { useCreatorAvatarEditor } from 'src/view/hooks/useCreatorAvatarEditor';
import Form from 'src/components/FormComponents/Form';
import { CreatorRoleSelect } from 'src/components/FormComponents/CreatorRoleSelect';
import CheckBox from 'src/components/FormComponents/CheckBox';
import {
    IsBoolean,
    IsEnum,
    IsOptional,
    IsString,
    Length,
    IsNotEmpty,
    IsEmail,
    ValidateIf,
    IsArray,
    ArrayUnique,
    IsUUID,
} from 'class-validator';
import { useSignInIdentities } from 'src/view/hooks/useSignInIdentities';
import { useTranslation } from 'react-i18next';
import i18n from 'src/i18n';
import { useCreatorRolePermission } from 'src/view/hooks';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import { OrganizationRolePermission } from 'src/domain/organization';
import { isOrganizationAdministrator } from 'src/domain/user';
import { dispatch } from 'src/state';
import { UserThunks } from 'src/state/thunks';
import { EmployeeDepartmentSelect } from 'src/components/FormComponents/EmployeeDepartmentSelect';
import { EmployeeTypeSelect } from 'src/components/FormComponents/EmployeeTypeSelect';
import {
    employeeDepartmentUpdate,
    LEGACY_DEPARTMENT_VALUE,
} from 'src/utils/employeeDepartment';

const {
    cradle: { munduClient },
} = container;

class EditCreatorSchema {
    @IsEnum(EmployeeType)
    employeeType: EmployeeType;

    @IsOptional()
    @IsString()
    departmentGroupId?: string;

    @IsOptional()
    @IsString({ message: () => i18n.t('validation.requiredField') })
    @Length(2, 30, {
        message: () => i18n.t('validation.lengthBetween', { min: 2, max: 30 }),
    })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    firstName?: string;

    @IsOptional()
    @IsString({ message: () => i18n.t('validation.requiredField') })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    lastName?: string;

    @IsOptional()
    @ValidateIf(o => o.email?.length !== 0)
    @IsString({ message: () => i18n.t('validation.invalidEmail') })
    @IsEmail({}, { message: () => i18n.t('validation.invalidEmail') })
    email?: string;

    @IsOptional()
    @IsArray()
    @ArrayUnique()
    @IsUUID('all', { each: true })
    roleIds?: string[];

    @IsOptional()
    @IsBoolean()
    organizationAdmin?: boolean;
}

export const CreatorProfileEdit = ({
    creator,
    onDelete,
}: {
    creator: Creator;
    onDelete?: () => void;
}) => {
    const notify = useNotification();
    const user = useSignedInUser();
    const { phoneNumber } = useSignInIdentities(creator.id);
    const { t } = useTranslation();
    const hasEditPermission = useCreatorRolePermission(
        OrganizationRolePermission.writeEmployees,
    );
    const canEdit = !user?.supportMode && hasEditPermission;
    const canManageAdmin =
        !user?.supportMode &&
        isOrganizationAdministrator(user, creator.organizationId);
    const isEditingSelf = user?.id === creator.id;
    const canEditCreator =
        canEdit && (!creator.organizationAdmin || canManageAdmin);
    const canDeleteCreator = canEditCreator && !isEditingSelf;
    const avatarEditor = useCreatorAvatarEditor(creator);
    const contacts = useStaffLoginContacts(
        'creators',
        creator.id,
        creator.organizationId,
        canEditCreator,
        {
            email: creator.email,
            phone: creator.phoneNumber,
            pending: creator.hasEverLoggedIn === false,
        },
    );

    const handleUpdate = async (data: EditCreatorSchema) => {
        if (!canEditCreator) return;
        try {
            // Contacts are saved first; a refused contact keeps the form open with its error.
            if (!(await contacts.save())) return;
            const avatar = await avatarEditor.getAvatarUpdate();
            const { organizationAdmin, departmentGroupId, ...employeeData } =
                data;
            const originalDepartment =
                creator.departmentGroupId ??
                (creator.department ? LEGACY_DEPARTMENT_VALUE : '');
            const payload = {
                ...employeeData,
                organizationId: creator.organizationId,
                ...employeeDepartmentUpdate(
                    departmentGroupId,
                    departmentGroupId !== originalDepartment,
                ),
                avatar,
                roleIds: employeeData.roleIds?.length
                    ? employeeData.roleIds
                    : undefined,
            };
            await munduClient.updateCreator(creator.id, payload);

            if (
                canManageAdmin &&
                !isEditingSelf &&
                (organizationAdmin === true) !==
                    (creator.organizationAdmin === true)
            ) {
                await munduClient.updateCreatorOrganizationAdmin(
                    creator.id,
                    creator.organizationId,
                    organizationAdmin === true,
                );
            }

            await mutate([creator.id, 'creators/creatorId']);
            await mutate(['creators/organizationId', creator.organizationId]);
            if (isEditingSelf) {
                await dispatch(UserThunks.refreshUser());
            }
            avatarEditor.reset();
            notify(t('notifications.profileUpdated'));
        } catch (e) {
            if (e instanceof Error) notify(e.message, true);
        }
    };

    return (
        <div className="bg-lightPeach flex flex-col gap-8">
            <Section title={t('creators.profile.editTitle')}>
                <Form
                    className="bg-lightPeach text-woodyBrown"
                    schema={EditCreatorSchema}
                    onSubmit={handleUpdate}
                    mode="onChange"
                    defaultValues={{
                        firstName: creator.profile.firstName,
                        lastName: creator.profile.lastName,
                        roleIds: creator.roleIds ?? [],
                        organizationAdmin: creator.organizationAdmin === true,
                        employeeType:
                            creator.employeeType ?? EmployeeType.fullTime,
                        departmentGroupId:
                            creator.departmentGroupId ??
                            (creator.department ? LEGACY_DEPARTMENT_VALUE : ''),
                    }}
                >
                    {({
                        register,
                        control,
                        clearErrors,
                        formState: { errors, isSubmitting },
                    }) => (
                        <div className="grid grid-cols-2 gap-4 h-full">
                            <div className="col-span-full flex justify-center">
                                <CreatorAvatarInput
                                    editor={avatarEditor}
                                    disabled={!canEditCreator || isSubmitting}
                                />
                            </div>

                            <Input
                                className="text-input"
                                type="text"
                                label={t('profile.labels.firstName')}
                                error={errors.firstName}
                                disabled={!canEditCreator}
                                {...register('firstName')}
                            />
                            <Input
                                className="text-input"
                                type="text"
                                label={t('profile.labels.lastName')}
                                error={errors.lastName}
                                disabled={!canEditCreator}
                                {...register('lastName')}
                            />
                            {canEditCreator ? (
                                <StaffContactFields
                                    contacts={contacts}
                                    disabled={isSubmitting}
                                    labels={{
                                        email: t('profile.labels.email'),
                                        phone: t('profile.labels.phone'),
                                    }}
                                />
                            ) : (
                                <>
                                    <Input
                                        className="text-input"
                                        type="tel"
                                        label={t('profile.labels.phone')}
                                        leadingIcon={PhoneIcon}
                                        value={phoneNumber || ''}
                                        readOnly
                                    />
                                    <Input
                                        className="text-input"
                                        type="email"
                                        label={t('profile.labels.email')}
                                        leadingIcon={EnvelopeIcon}
                                        value={creator.email ?? ''}
                                        readOnly
                                    />
                                </>
                            )}
                            <EmployeeDepartmentSelect
                                control={control}
                                name="departmentGroupId"
                                organizationId={creator.organizationId}
                                legacyDepartment={creator.department}
                                disabled={!canEditCreator || isSubmitting}
                            />
                            <EmployeeTypeSelect
                                control={control}
                                name="employeeType"
                                disabled={!canEditCreator || isSubmitting}
                            />
                            <CreatorRoleSelect
                                control={control}
                                name={'roleIds'}
                                organizationId={creator.organizationId}
                                error={errors.roleIds}
                                clearErrors={clearErrors}
                                disabled={
                                    !creator.organizationId || !canEditCreator
                                }
                            />
                            {canManageAdmin && (
                                <CheckBox
                                    className="col-span-full"
                                    label={t('roles.systemAdmin')}
                                    helperText={
                                        isEditingSelf
                                            ? t(
                                                  'creators.form.adminSelfRemoval',
                                              )
                                            : t(
                                                  'creators.form.adminDescription',
                                              )
                                    }
                                    disabled={isEditingSelf}
                                    {...register('organizationAdmin')}
                                />
                            )}

                            {canEditCreator && (
                                <div className="col-span-full flex justify-end">
                                    <Input
                                        value={t('common.save')}
                                        className="primary-btn-small"
                                        disabled={isSubmitting}
                                        type="submit"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </Form>
            </Section>

            {canDeleteCreator && (
                <Section title={t('creators.profile.deleteTitle')}>
                    <DeleteCreator creator={creator} onDelete={onDelete} />
                </Section>
            )}
        </div>
    );
};

const DeleteCreator = ({
    creator,
    onDelete,
}: {
    creator: Creator;
    onDelete?: () => void;
}) => {
    const [confirmLastName, setConfirmLastName] = useState('');
    const [canDelete, setCanDelete] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const notify = useNotification();
    const { t } = useTranslation();

    const handleDelete = async () => {
        try {
            setIsDeleting(true);
            await munduClient.deleteCreator(creator.id);
            await mutate(['creators/organizationId', creator.organizationId]);
            notify(t('creators.notifications.deleted'));
            onDelete?.();
        } catch (e) {
            console.error(e);
            if (e instanceof Error) notify(e.message, true);
        } finally {
            setIsDeleting(false);
        }
    };

    useEffect(() => {
        if (confirmLastName === creator.profile.lastName) {
            setCanDelete(true);
        } else {
            setCanDelete(false);
        }
    }, [confirmLastName, creator.profile.lastName]);

    return (
        <>
            <div>{t('creators.profile.deleteDescription')}</div>
            <div className="self-start flex items-end gap-3 flex-wrap">
                <Input
                    className="text-input"
                    type="email"
                    onChange={e => setConfirmLastName(e.currentTarget.value)}
                    label={t('creators.profile.confirmLabel', {
                        value: creator.profile.lastName,
                    })}
                    leadingIcon={EnvelopeIcon}
                />
                <Button
                    title={
                        isDeleting
                            ? t('common.loading')
                            : t('creators.profile.deleteButton')
                    }
                    onClick={handleDelete}
                    disabled={!canDelete || isDeleting}
                />
            </div>
        </>
    );
};
